import {logger} from "@/components/DownloaderApp/logger";
import {
    _fetchTextWithHeaders,
    calculateVideoSize2,
    errorMessageOf,
    isDev
} from "@/components/DownloaderApp/utils";
import {isWriteAbortedError} from "@/components/DownloaderApp/download-errors";
import {getVariantPlaylistUrl} from "@/components/DownloaderApp/playlists";
import {createTsRemuxer} from "@/components/DownloaderApp/ts-remux-v2";
import {createTsRemuxerV3} from "@/components/DownloaderApp/ts-remux-v3";
import Queue from "better-queue";
import * as R from "remeda";
import throttle from 'lodash.throttle';
import mitt from 'mitt'

import MemoryStore from "better-queue-memory";
import {parse} from "hls-parser";

const DOWNLOAD_STATE_KEY = "downloadPlaylistChunks:active-download";

// Firefox has no showSaveFilePicker, so it uses the StreamSaver writer. That
// writer can abort a write mid-download, and Brave can reject with no reason.
// Retry a few times using the segments already fetched instead of failing.
const WRITE_ABORT_MAX_RETRIES = 3;
const WRITE_ABORT_RETRY_DELAY_MS = 500;

function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function getDeviceInfo() {
    const navigatorWithDeviceInfo = navigator as Navigator & {
        deviceMemory?: number;
        connection?: {
            effectiveType?: string;
            downlink?: number;
            rtt?: number;
            saveData?: boolean;
        };
    };

    return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        deviceMemoryGb: navigatorWithDeviceInfo.deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        maxTouchPoints: navigator.maxTouchPoints,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        devicePixelRatio: window.devicePixelRatio,
        connectionEffectiveType: navigatorWithDeviceInfo.connection?.effectiveType,
        connectionDownlinkMbps: navigatorWithDeviceInfo.connection?.downlink,
        connectionRttMs: navigatorWithDeviceInfo.connection?.rtt,
        connectionSaveData: navigatorWithDeviceInfo.connection?.saveData,
    };
}

export function reportInterruptedPlaylistDownload() {
    const stateText = sessionStorage.getItem(DOWNLOAD_STATE_KEY);
    if (!stateText) return;

    sessionStorage.removeItem(DOWNLOAD_STATE_KEY);

    try {
        const state = JSON.parse(stateText);
        const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        const details = {
            ...state,
            navigationType: navigation?.type,
            documentWasDiscarded: (document as Document & {wasDiscarded?: boolean}).wasDiscarded ?? false,
            detectedAt: new Date().toISOString(),
            deviceAtDetection: getDeviceInfo(),
        };

        console.error("Previous playlist download was interrupted", details);
        window.posthog?.logger.error("downloadPlaylistChunks:previous-download-interrupted", details);
    } catch (err) {
        console.error("Failed to parse previous playlist download state", err);
    }
}

export function downloadPlaylistChunks({
                                           log,
                                           trim,
                                           isLive,
                                           updateMonitoringContext,
                                           variantPlaylistText,
                                           variantPlaylistUrl,
                                           fetchWitchExtension,
                                           onProgressUpdated,
                                           writableStream,
                                           uniqueSegmentsOnly,
                                           onSegmentFetchFailed,
                                           remux = null,
                                           outputExtension = ".ts",
                                           track,
                                           setEstimated: _setEstimated
                                       }) {
    const emitter = mitt()

    const downloadPlaylistChunksLog = log || {};
    const mediaRemuxer =
        outputExtension === ".vtt"
            ? null
            : remux === "v3"
                ? createTsRemuxerV3()
                : remux === "v2"
                    ? createTsRemuxer()
                    : null;

    let livePlaylistFetcherInterval = null;
    let TOTAL_SEGMENTS = -1;
    let HANDLED_SEGMENTS = [];
    const fetchedChunks = [];
    let totalBytesDownloaded = 0;
    let currentPercentageProgress = 0;

    let stopDownloading = false;
    const startedAt = new Date().toISOString();
    const deviceAtDownloadStart = getDeviceInfo();

    function stashDownloadState(status, additionalState = {}) {
        try {
            sessionStorage.setItem(DOWNLOAD_STATE_KEY, JSON.stringify({
                status,
                startedAt,
                updatedAt: new Date().toISOString(),
                variantPlaylistUrl,
                fetchWitchExtension,
                isLive,
                totalSegments: TOTAL_SEGMENTS,
                totalSegmentsFetched: HANDLED_SEGMENTS.length,
                downloadedChunks: HANDLED_SEGMENTS.length,
                totalBytesDownloaded,
                totalMbDownloaded: +(totalBytesDownloaded / 1024 / 1024).toFixed(2),
                progress: currentPercentageProgress,
                deviceAtDownloadStart,
                ...additionalState,
            }));
        } catch (err) {
            console.error("Failed to stash playlist download state", err);
        }
    }

    function clearDownloadState() {
        sessionStorage.removeItem(DOWNLOAD_STATE_KEY);
        window.removeEventListener("pagehide", recordPagehide);
    }

    function recordPagehide() {
        stashDownloadState("pagehide", {
            pagehideObserved: true,
            pagehideAt: new Date().toISOString(),
            queueStats: q.getStats(),
        });
    }

    const setEstimated = throttle(_setEstimated, 1000 * 5)

    const q = new Queue(
        async function ({segment}, cb) {
            try {
                const fetchUrl = getVariantPlaylistUrl(segment.uri, variantPlaylistUrl);
                stashDownloadState("fetching-segment", {
                    segmentMediaSequenceNumber: segment.mediaSequenceNumber,
                    fetchUrl,
                });
                if (fetchWitchExtension) {
                    const buffer = await window.sendMessage("fetch-segment", fetchUrl)
                    stashDownloadState("creating-uint8-array", {
                        segmentMediaSequenceNumber: segment.mediaSequenceNumber,
                        bufferByteLength: buffer?.byteLength,
                    });
                    // logger.log("fetched chunk DownloadApp.", buffer)
                    const data = new Uint8Array(buffer);
                    fetchedChunks[segment.mediaSequenceNumber] = {data, done: false};
                    totalBytesDownloaded += data.byteLength;
                    HANDLED_SEGMENTS.push(segment.mediaSequenceNumber);

                    return cb(null, segment.mediaSequenceNumber);
                }

                const response = await fetch(fetchUrl);
                if (!response.ok) {
                    logger.log(response)
                    throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
                }
                stashDownloadState("reading-response-buffer", {
                    segmentMediaSequenceNumber: segment.mediaSequenceNumber,
                    responseStatus: response.status,
                    responseContentLength: response.headers.get("content-length"),
                });
                const buffer = await response.arrayBuffer();
                stashDownloadState("creating-uint8-array", {
                    segmentMediaSequenceNumber: segment.mediaSequenceNumber,
                    bufferByteLength: buffer.byteLength,
                });
                const data = new Uint8Array(buffer);
                logger.log({segment})
                fetchedChunks[segment.mediaSequenceNumber] = {data, done: false};
                totalBytesDownloaded += data.byteLength;
                HANDLED_SEGMENTS.push(segment.mediaSequenceNumber);

                cb(null, segment.mediaSequenceNumber);
            } catch (err) {
                isDev && logger.log("failed", {err, segment});

                emitter.emit("segment-fetch-failed", {
                    message: err.message,
                    segment,
                    progress: currentPercentageProgress,
                    totalSegmentsFetched: HANDLED_SEGMENTS.length,
                });
                cb(err);
            }
        },
        {
            priority: ({segment}, cb) => {
                cb(null, -segment.mediaSequenceNumber);
            },
            setImmediate: (cb) => setTimeout(cb, 0),
            concurrent: 4,
            store: new MemoryStore(),
            maxRetries: 30,
            retryDelay: 1000 * 10,
            maxTimeout: 1000 * 60 * 8,
        }
    );
    window.q = q;
    window.conclude = conclude;


    function conclude() {
        stopDownloading = true;
        return proceedWithSave()
    }

    emitter.on("conclude", conclude);

    function cleanup() {
        clearInterval(livePlaylistFetcherInterval)
        q.destroy()
    }

    const variantUrlPrefix = variantPlaylistUrl.replace("/" + variantPlaylistUrl.split('/').pop(), "");
    downloadPlaylistChunksLog.variantUrlPrefix=variantUrlPrefix;
    const playlist = parse(variantPlaylistText);



    if (uniqueSegmentsOnly) {
        playlist.segments = R.uniqueBy(playlist.segments, a => a.uri).map((segment, i) => ({
            ...segment,
            mediaSequenceNumber: i
        }));
    }
    // playlist.segments = _.take(playlist.segments, 10)
    if (trim) {
        const [from, to] = trim;
        playlist.segments = playlist.segments.filter((segment, index) => (from <= index && index < to))
    }

    console.log(playlist.segments)
    downloadPlaylistChunksLog.segments = playlist.segments?.length;
    downloadPlaylistChunksLog.isLive = isLive;


    updateMonitoringContext({
        totalSegments: playlist.segments.length,
        remux: !!mediaRemuxer,
        discontinuityPresent: playlist.segments.some(segment => segment.discontinuitySequence !== 0)
    });

    let lastHandledMediaSequenceNumber = 0;


    let syncFrom = 0;


    if (isLive) {
        let REFETCH_PLAYLIST_TIMEOUT_MS = 60 * 1000;
        let lastSegmentsUpdate = Date.now()
        if (isDev) {
            syncFrom = playlist.segments.length - 5;
        }
        console.log({syncFrom})
        playlist.segments = playlist.segments.filter((segment, index) => (syncFrom <= index))

        function recordPullLiveSegmentsError(err) {
            downloadPlaylistChunksLog.pullLiveSegmentsErrorMessage = err?.message || 'no-message-defiend';
            downloadPlaylistChunksLog.pullLiveSegmentsError = err;
        }

        pullLiveSegments().catch(recordPullLiveSegmentsError)

        async function pullLiveSegments() {
            const segmentsProgress = {
                downloaded: q.getStats().total + syncFrom,
                totalSegments: syncFrom + TOTAL_SEGMENTS
            };
            console.log('segmentsProgress', segmentsProgress, REFETCH_PLAYLIST_TIMEOUT_MS);

            const segmentsDiff = segmentsProgress.totalSegments - segmentsProgress.downloaded;
            console.log({segmentsDiff})
            if (segmentsDiff > 20) {
                REFETCH_PLAYLIST_TIMEOUT_MS = 60 * 1000
            } else if (segmentsDiff < 20) {
                REFETCH_PLAYLIST_TIMEOUT_MS = 5 * 1000
            }

            livePlaylistFetcherInterval = setTimeout(() => {
                pullLiveSegments().catch(recordPullLiveSegmentsError)
            }, REFETCH_PLAYLIST_TIMEOUT_MS)

                console.log({variantPlaylistUrl})

                let plText;
                let headers;
                let fetchErr = '';
                if (fetchWitchExtension) {
                    plText = await window.sendMessage("fetch-text", variantPlaylistUrl);
                    headers = {
                        'last-modified': new Date().toISOString(),
                    }
                } else {
                    try {
                        const [_plText, _headers] = await _fetchTextWithHeaders(variantPlaylistUrl);
                        plText = _plText;
                        headers = _headers;
                    }catch (err) {
                        fetchErr = err?.message;
                    }
                }

                let updatedPlaylist
                try{
                    updatedPlaylist = parse(plText)
                } catch(e){
                    downloadPlaylistChunksLog.pullLiveSegmentsParsePlaylistError = {
                        plText,
                        fetchErr,
                        variantPlaylistUrl,
                        err: e?.message ?? ''
                    }
                    throw e;
                }


                updatedPlaylist.segments = updatedPlaylist.segments
                    .filter((segment, index) => (syncFrom <= index))
                    .map((segment, index) => {
                        segment.i = index;
                        return segment;
                    })
                    .filter((segment) => lastHandledMediaSequenceNumber < segment.mediaSequenceNumber)

                if (updatedPlaylist.segments.length) {
                    lastSegmentsUpdate = Date.now()
                    console.log('NEW SEGMENTS', updatedPlaylist.segments)
                    TOTAL_SEGMENTS = TOTAL_SEGMENTS + updatedPlaylist.segments.length;
                    updatePercentage()
                    setTimeout(() => {
                        playlist.segments = [...playlist.segments, ...updatedPlaylist.segments]
                        updatedPlaylist.segments.map(async (segment) => {
                            q.push({segment})
                            lastHandledMediaSequenceNumber = segment.mediaSequenceNumber;
                        });
                    })
                }
                console.log({
                    lastPlaylistModified: headers['last-modified'],
                    lastSegmentsUpdate: Math.round((Date.now() - lastSegmentsUpdate) / 1000),
                })
        }
    }
    TOTAL_SEGMENTS = playlist.segments.length
    stashDownloadState("downloading");
    window.addEventListener("pagehide", recordPagehide);

    playlist.segments.map(async (segment, i) => {
        segment.i = i;
        q.push({segment});
        lastHandledMediaSequenceNumber = segment.mediaSequenceNumber;
    });

    function updatePercentage() {
        const percentage = +(
            (HANDLED_SEGMENTS.length / TOTAL_SEGMENTS) *
            100
        ).toFixed(2);
        currentPercentageProgress = percentage;
        stashDownloadState("downloading", {
            queueStats: q.getStats(),
        });
        emitter.emit("progress", percentage);
    }

    let streamClosed = false;
    let saveOperation: "write" | "remux" | "subtitle" | "close" | "finalize" = "finalize";

    function failDownload(reason: unknown) {
        const fallbackMessages = {
            write: "Download aborted: your browser stopped writing the file without reporting a reason. Start the download again.",
            close: "Download aborted: your browser could not finish saving the file and did not report a reason. Start the download again.",
            remux: "Failed to process the video for saving. Start the download again or choose another format.",
            subtitle: "Failed to process the subtitles for saving. Start the download again.",
            finalize: "Failed to finish the download without an error description. Start the download again.",
        };
        const message = reason == null ? "" : errorMessageOf(reason);
        const name = reason && typeof reason === "object" && "name" in reason && typeof reason.name === "string"
            ? reason.name
            : "PlaylistDownloadError";
        const error = Object.assign(new Error(message || fallbackMessages[saveOperation]), {
            name,
            cause: reason,
            operation: saveOperation,
        });

        downloadPlaylistChunksLog.saveErrorOperation = saveOperation;
        downloadPlaylistChunksLog.saveErrorReasonType = reason === null ? "null" : typeof reason;
        downloadPlaylistChunksLog.saveErrorReason = reason;
        downloadPlaylistChunksLog.saveErrorMessage = error.message;
        downloadPlaylistChunksLog.saveErrorName = error.name;
        downloadPlaylistChunksLog.saveErrorStack = reason instanceof Error ? reason.stack : error.stack;
        streamClosed = true;
        clearDownloadState();
        cleanup();
        emitter.emit("error", error);
    }

    // Serializes all write/close operations on writableStream to prevent
    // "state cached in an interface object" errors from concurrent access.
    let writeChain = Promise.resolve();

    function enqueueStreamOp(fn) {
        writeChain = writeChain.then(fn, fn);
        return writeChain;
    }

    downloadPlaylistChunksLog.writeSegmentSuccessCount = 0;
    downloadPlaylistChunksLog.writeSegmentFailureCount = 0;
    let subtitleHeaderWritten = false;

    async function writePayload(payload) {
        if (payload.length === 0) return;
        saveOperation = "write";

        for (let attempt = 0; ; attempt++) {
            if (streamClosed) return;
            try {
                const result = await writableStream.write(payload);
                downloadPlaylistChunksLog.writeSegmentSuccessCount++;
                return result;
            } catch (err) {
                downloadPlaylistChunksLog.writeSegmentFailureCount++;
                const isRetryable = err == null || isWriteAbortedError(err);
                if (streamClosed || attempt >= WRITE_ABORT_MAX_RETRIES || !isRetryable) {
                    downloadPlaylistChunksLog.writeSegmentFailureErrorMessage = err?.message;
                    downloadPlaylistChunksLog.writeSegmentFailureError = err;
                    throw err;
                }
                logger.log("writableStream.write aborted, retrying to resume download", {attempt, err});
                await delay(WRITE_ABORT_RETRY_DELAY_MS);
            }
        }
    }

    async function writeSegment(data) {
        if (outputExtension === ".vtt") {
            saveOperation = "subtitle";
            const text = new TextDecoder("utf-8", {fatal: true}).decode(data)
                .split(/\r\n|\r|\n/)
                .filter(line => !/^WEBVTT(?:[ \t]|$)/.test(line) && !line.startsWith("X-TIMESTAMP-MAP="))
                .join("\n")
                .replace(/^\n+|\n+$/g, "");
            const header = subtitleHeaderWritten ? "" : "WEBVTT\n\n";
            const payload = new TextEncoder().encode(header + text + "\n\n");
            await writePayload(payload);
            subtitleHeaderWritten = true;
            return payload.length;
        }

        // Transform once, before the retry loop, so a retried write does not re-remux.
        saveOperation = "remux";
        const payload = mediaRemuxer ? mediaRemuxer.transform(data) : data;
        await writePayload(payload);
        return payload.length;
    }

    async function finish() {
        if (streamClosed) return;
        saveOperation = "finalize";

        // Everything from here until close() resolves is the "finalizing..." phase the
        // user sees: the browser is still committing buffered bytes to disk.
        const finalizingStartedAt = performance.now();

        updateMonitoringContext({ totalSegmentsFetched: HANDLED_SEGMENTS.length})

        // Any trailing bytes that never completed a packet still belong in the file.
        if (mediaRemuxer) {
            saveOperation = "remux";
            const tail = mediaRemuxer.flush();
            if (tail.length > 0) {
                await writePayload(tail);
            }
            updateMonitoringContext({remuxStats: mediaRemuxer.stats});
            logger.log("media remux stats", mediaRemuxer.stats);
        }

        streamClosed = true;
        saveOperation = "close";
        await writableStream.close();
        saveOperation = "finalize";

        const finalizingSeconds = Number(((performance.now() - finalizingStartedAt) / 1000).toFixed(2));
        // Set before emitting success, so the "saved" state event carries it.
        updateMonitoringContext({finalizingSeconds});
        logger.log("finalizing done", {finalizingSeconds});

        clearDownloadState();
        emitter.emit("success", {
            writableStream,
            segments: playlist.segments
        })
        cleanup();
    }

    let isInProgress = false;
    let totalSize = 0;

    async function proceedWithSave() {
        if (streamClosed) return;
        try {
            for (const [_, {mediaSequenceNumber: i}] of playlist.segments.entries()) {
                if (!fetchedChunks[i]) {
                    return
                }
                const {done, data} = fetchedChunks[i];
                if (done) {
                    continue
                }
                if (!done) {
                    const bytesWritten = await enqueueStreamOp(() => writeSegment(data));
                    totalSize += bytesWritten;
                    fetchedChunks[i].done = true;
                    delete fetchedChunks[i].data
                }
            }
            if (isLive && !stopDownloading) {
                return console.log('prevent saving...')
            }

            updateMonitoringContext({totalBytes: totalSize, totalSizeFormatted: calculateVideoSize2(totalSize)})
            await enqueueStreamOp(() => finish())
        } catch (err) {
            failDownload(err);
        }
    }

    q.on('task_started', (id, {segment}) => {
        if (segment.i === 0) {
            emitter.emit("progress", 0);
        }
    })


    q.on("task_finish", async function (taskId, result) {
        updatePercentage()

        isDev && logger.log("task_finish", q.getStats(), result, {
            HANDLED_SEGMENTS: HANDLED_SEGMENTS.length,
            TOTAL_SEGMENTS
        });
        if (!isInProgress) {
            isInProgress = true;
            await proceedWithSave();
            isInProgress = false;

            const totalSegmentsDone = fetchedChunks.filter(a => a.done).length
            if (totalSegmentsDone === 0) return;
            const estimated = calculateVideoSize2(TOTAL_SEGMENTS * totalSize / totalSegmentsDone)
            setEstimated(TOTAL_SEGMENTS * totalSize / totalSegmentsDone);
            updateMonitoringContext({totalBytes: totalSize, totalSizeFormatted: calculateVideoSize2(totalSize)})
            isDev && logger.log({estimated, totalSegmentsDone, totalSize});
        }
    });
    q.on("drain", async function () {
        const queueStats = q.getStats();
        isDev && logger.log('=== done > REAL PROGRESS', queueStats.total, TOTAL_SEGMENTS, queueStats);
        if (queueStats.total === TOTAL_SEGMENTS && queueStats.successRate !== 1) {
            try {
                await enqueueStreamOp(async () => {
                    if (streamClosed) return;
                    streamClosed = true;
                    saveOperation = "close";
                    await writableStream.close();
                });
            } catch (err) {
                failDownload(err);
                return;
            }
            saveOperation = "finalize";
            failDownload(new Error("Failed to fetch all segments after retries"));
        }
    });

    return emitter
}